import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { formatPrice } from '@/components/ui/Price';

interface Props {
  /** `session.paidTotal` — déjà encaissé sur cette table. */
  paidTotal: string | undefined;
  /** `session.dueTotal` — reste à percevoir. */
  dueTotal: string | undefined;
  /** `session.orderingLocked` — le convive a soldé, sa commande est fermée. */
  orderingLocked: boolean;
  onReopen: () => void;
  busy?: boolean;
}

function cents(value: string | undefined): number {
  return Math.round(parseFloat(value ?? '0') * 100);
}

/**
 * Spec 2026-08-28-dine-in-paid-table-ordering-lock §5.4 — la barre d'état d'une
 * table qui a payé.
 *
 * Deux situations, et elles peuvent coexister : la table est verrouillée (le
 * convive a soldé, il ne commande plus), et/ou elle doit à nouveau de l'argent
 * après avoir payé (un serveur peut servir une table verrouillée — c'est tout
 * l'intérêt de l'exemption `skip_paid_lock`). Le reste à percevoir prime dans
 * la phrase : c'est le chiffre qu'on réclame.
 */
export default function TicketPaidBanner({
  paidTotal,
  dueTotal,
  orderingLocked,
  onReopen,
  busy = false,
}: Props) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  const paid = cents(paidTotal);
  const due = cents(dueTotal);
  const relance = paid > 0 && due > 0;
  if (!orderingLocked && !relance) return null;

  const message = relance
    ? t('waiter.ticket.paid.relance', {
        paid: formatPrice(paid),
        due: formatPrice(due),
      })
    : t('waiter.ticket.paid.settled', { paid: formatPrice(paid) });

  return (
    <>
      <div
        data-testid="ticket-paid-banner"
        role="status"
        className="flex items-center justify-between gap-2 px-3 py-2 border-b-2 border-hairline"
        style={{
          backgroundColor: relance ? 'var(--w-pending-bg)' : 'var(--w-state-paid-bg)',
          color: relance ? 'var(--w-pending-fg)' : 'var(--w-state-paid-fg)',
        }}
      >
        <span className="font-sans text-[13px] font-semibold min-w-0">{message}</span>
        {orderingLocked && (
          <button
            type="button"
            data-testid="ticket-reopen-ordering"
            onClick={() => setConfirming(true)}
            className="shrink-0 bg-paper border-2 border-ink rounded-btn px-3 py-1.5 font-sans text-[13px] font-semibold text-ink"
          >
            {t('waiter.ticket.paid.reopen')}
          </button>
        )}
      </div>
      {/* Modale CENTRÉE (spec salle §6). Rouvrir rend la table à nouveau
          redevable : c'est une décision, pas un réglage. */}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        ariaLabel={t('waiter.ticket.paid.reopenConfirmTitle')}
      >
        <div data-testid="ticket-reopen-confirm" className="p-5 flex flex-col gap-4">
          <h2 className="font-display text-lg">{t('waiter.ticket.paid.reopenConfirmTitle')}</h2>
          <p className="font-mono text-sm text-ink/70">
            {t('waiter.ticket.paid.reopenConfirmBody')}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="ticket-reopen-cancel"
              onClick={() => setConfirming(false)}
              className="flex-1 bg-paper border-2 border-ink rounded-btn px-4 py-2 font-display"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              data-testid="ticket-reopen-confirm-button"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                onReopen();
              }}
              className="flex-1 bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-4 py-2 font-display disabled:opacity-50"
            >
              {t('waiter.ticket.paid.reopen')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
